/**
 * MoneyGram Send Commit Service Implementation
 * Handles transaction commit operations
 */

import type { AxiosInstance } from "axios";
import type { SendCommitService } from "../contracts/send-commit.contract";
import type { RequestConfig } from "../types/common";
import type {
	SendCommitRequest,
	SendCommitResponse,
	ValidationResponse,
} from "../types/moneygram";

export class MoneyGramSendCommitService implements SendCommitService {
	constructor(private readonly httpClient: AxiosInstance) {}

	/**
	 * Validate transaction data (PUT request)
	 */
	async validateTransaction(
		transactionId: string,
		request: SendCommitRequest,
		config?: RequestConfig,
	): Promise<ValidationResponse> {
		try {
			// Validate required parameters
			this.validateSendCommitRequest(transactionId, request);

			const response = await this.httpClient.put(
				`/transactions/${transactionId}`,
				request,
				{
					params: {
						operation: config?.params?.operation || "withdraw",
						...config?.params,
					},
					signal: config?.signal,
					timeout: config?.timeout,
					...config,
				},
			);

			return this.transformValidationResponse(response.data);
		} catch (error) {
			throw this.handleApiError(error, "Failed to validate transaction");
		}
	}

	/**
	 * Final commit of transaction (POST request)
	 */
	async commitTransaction(
		transactionId: string,
		config?: RequestConfig,
	): Promise<SendCommitResponse> {
		try {
			// Validate transaction ID
			if (!transactionId || typeof transactionId !== "string") {
				throw new Error("Transaction ID is required and must be a string");
			}

			// Required payload for commit
			const commitPayload = { userLanguage: "en-US" };

			const response = await this.httpClient.post(
				`/transactions/${transactionId}/commit`,
				commitPayload, // Required payload for commit
				{
					params: {
						isQrCodeRequired: config?.params?.isQrCodeRequired ?? false,
						...config?.params,
					},
					signal: config?.signal,
					timeout: config?.timeout,
					...config,
				},
			);

			return this.transformSendCommitResponse(response.data);
		} catch (error) {
			// Handle commit error
			throw this.handleApiError(error, "Failed to commit transaction");
		}
	}

	/**
	 * Validate send commit request requirements
	 */
	private validateSendCommitRequest(
		transactionId: string,
		request: SendCommitRequest,
	): void {
		if (!transactionId || typeof transactionId !== "string") {
			throw new Error("Transaction ID is required and must be a string");
		}

		if (!request) {
			throw new Error("Request payload is required");
		}

		if (!request.userLanguage || typeof request.userLanguage !== "string") {
			throw new Error("userLanguage is required and must be a string");
		}

		// Check payload type based on content
		const returningUserRequest = request as SendCommitRequest & {
			digitalProfileId?: string;
		};
		const hasDigitalProfileId = !!returningUserRequest.digitalProfileId;
		const hasSenderInfo = !!request.sender;

		if (hasDigitalProfileId && !hasSenderInfo) {
			// Scenario 1: Returning user using saved account (simplified payload)
			// Has: digitalProfileId + riskStatus
			// API will automatically look up saved bank account from the profile
			// No sender details, no targetAccountProfileId needed
		} else if (hasDigitalProfileId && hasSenderInfo) {
			// Scenario 2: Returning user editing/adding new bank account
			// Has: digitalProfileId + full sender details + bank account fields
			// This tells API to update/save the new bank details to the profile
			if (!request.sender.firstName || !request.sender.lastName) {
				throw new Error("sender firstName and lastName are required");
			}
		} else if (hasSenderInfo) {
			// Scenario 3: New user (standard flow)
			// Has: sender details + bank account fields (if bank service)
			// No digitalProfileId
			if (!request.sender.firstName || !request.sender.lastName) {
				throw new Error("sender firstName and lastName are required");
			}
		} else {
			throw new Error(
				"Invalid payload: missing required sender information or account details",
			);
		}

		if (!request.sendAmount || !request.sendAmount.value) {
			throw new Error("sendAmount is required");
		}
	}

	/**
	 * Transform validation API response
	 */
	private transformValidationResponse(data: unknown): ValidationResponse {
		// Basic validation and transformation
		if (!data || typeof data !== "object") {
			throw new Error("Invalid response format from Validation API");
		}

		// Return the data as-is - the API should return the proper ValidationResponse format
		return data as ValidationResponse;
	}

	/**
	 * Transform send commit API response
	 */
	private transformSendCommitResponse(apiData: unknown): SendCommitResponse {
		const responseData = apiData as Record<string, unknown>;

		if (!responseData || typeof responseData !== "object") {
			throw new Error("Invalid send commit response format");
		}

		// Validate required fields in response
		if (!responseData.transactionId) {
			throw new Error("Missing transactionId in response");
		}

		if (!responseData.transactionCryptoNetworkInstructions) {
			throw new Error(
				"Missing transactionCryptoNetworkInstructions in response",
			);
		}

		// Check for v5 format (without sep24TransactionResponse)
		if (!responseData.sep24TransactionResponse) {
			console.warn(
				"[Commit Response] v5 format detected: sep24TransactionResponse not present",
			);
		}

		// Deposit flow uses confirmationCode, withdraw flow uses referenceNumber
		if (!responseData.referenceNumber && !responseData.confirmationCode) {
			throw new Error(
				"Missing referenceNumber or confirmationCode in response",
			);
		}

		// Transform to SendCommitResponse structure
		const cryptoInstructions =
			responseData.transactionCryptoNetworkInstructions as Record<
				string,
				unknown
			>;

		// Build response object - include either referenceNumber or confirmationCode
		const response: SendCommitResponse = {
			transactionId: String(responseData.transactionId),
			transactionCryptoNetworkInstructions: {
				accountAddress: String(cryptoInstructions.accountAddress || ""),
				operationMemo: String(cryptoInstructions.operationMemo || ""),
			},
			// Include referenceNumber if present (withdraw flow)
			...(responseData.referenceNumber
				? { referenceNumber: String(responseData.referenceNumber) }
				: {}),
			// Include confirmationCode if present (deposit flow)
			...(responseData.confirmationCode
				? { confirmationCode: String(responseData.confirmationCode) }
				: {}),
		};

		// Capture top-level status if present (future v5 enhancement)
		if (responseData.status) {
			response.status = String(responseData.status);
			console.log(
				"[Commit Response] Top-level status detected:",
				response.status,
			);
		}

		// Only include sep24TransactionResponse if present (v4 format)
		if (responseData.sep24TransactionResponse) {
			const sep24Response = responseData.sep24TransactionResponse as Record<
				string,
				unknown
			>;

			// Helper to extract amount from either object { amount, asset } or string format
			const extractAmount = (
				value: unknown,
			): { amount: string; asset: string } => {
				if (typeof value === "object" && value !== null) {
					const obj = value as Record<string, unknown>;
					return {
						amount: String(obj.amount || "0"),
						asset: String(obj.asset || ""),
					};
				}
				return { amount: String(value || "0"), asset: "" };
			};

			// Parse amounts - API may return as object { amount, asset } or as separate fields
			const amountIn = extractAmount(sep24Response.amount_in);
			const amountOut = extractAmount(sep24Response.amount_out);
			const amountFee = extractAmount(sep24Response.amount_fee);

			response.sep24TransactionResponse = {
				withdraw_anchor_account: String(
					sep24Response.withdraw_anchor_account || "",
				),
				withdraw_memo: String(sep24Response.withdraw_memo || ""),
				withdraw_memo_type: String(sep24Response.withdraw_memo_type || ""),
				id: String(sep24Response.id || ""),
				kind: String(sep24Response.kind || ""),
				status: String(sep24Response.status || ""),
				more_info_url: String(sep24Response.more_info_url || ""),
				// Use parsed amounts - prefer object format, fallback to separate fields
				amount_in: amountIn.amount,
				amount_in_asset:
					amountIn.asset || String(sep24Response.amount_in_asset || ""),
				amount_out: amountOut.amount,
				amount_out_asset:
					amountOut.asset || String(sep24Response.amount_out_asset || ""),
				amount_fee: amountFee.amount,
				amount_fee_asset:
					amountFee.asset || String(sep24Response.amount_fee_asset || ""),
				started_at: String(sep24Response.started_at || ""),
				stellar_transaction_id: String(
					sep24Response.stellar_transaction_id || "",
				),
				external_transaction_id: String(
					sep24Response.external_transaction_id || "",
				),
				refunded: Boolean(sep24Response.refunded),
				from: String(sep24Response.from || ""),
				to: String(sep24Response.to || ""),
			};
		}

		return response;
	}

	/**
	 * Handle and transform API errors
	 */
	private handleApiError(error: unknown, defaultMessage: string): Error {
		if (error instanceof Error) {
			return new Error(`${defaultMessage}: ${error.message}`);
		}

		// Handle axios errors specifically
		if (typeof error === "object" && error && "response" in error) {
			const axiosError = error as {
				response: { data?: { message?: string }; status?: number };
			};
			const status = axiosError.response?.status;
			const message = axiosError.response?.data?.message || defaultMessage;
			return new Error(`${message} (Status: ${status})`);
		}

		// Handle network errors
		if (typeof error === "object" && error && "code" in error) {
			const networkError = error as { code: string };
			return new Error(
				`${defaultMessage}: Network error (${networkError.code})`,
			);
		}

		return new Error(defaultMessage);
	}
}
